import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Network,
  RefreshCw,
  Users,
  Check,
  AlertCircle,
  CheckCircle2,
  Search,
  UserCheck,
  ChevronRight,
  Plus,
  Trash2,
  Store,
  Mail,
  Phone,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { SlidePanel } from '../components/SlidePanel';
import { GET_DISTRIBUTORS, GET_FSRS, SET_FSR_DISTRIBUTORS } from '../graphql/queries';

interface LiveFsr {
  id: string;
  name: string;
  employeeId: string;
  avatar: string | null;
  color: string | null;
  assignedDistributors: { distributorId: string; catalogAccess: 'full' | 'collection' }[];
}

interface LiveDistributor {
  id: string;
  name: string;
  city: string;
  email: string | null;
  mobile: string | null;
  status: string | null;
  avatar: string;
  color: string;
  // Local-only assignment overlay — persisted assignment is a future slice.
  officerId?: string | null;
  officerName?: string | null;
  catalogAccess?: 'full' | 'collection';
}

const COLOR_PALETTE = ['#4F46E5', '#7C3AED', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#06B6D4'];

const initialsOf = (name?: string | null) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const colorFor = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return COLOR_PALETTE[h % COLOR_PALETTE.length];
};

interface Props {
  setTitle: (t: string) => void;
  setSubtitle: (s: string) => void;
  showToast: (m: string, t?: any) => void;
}

export const DistributorAssignment: React.FC<Props> = ({ setTitle, setSubtitle, showToast }) => {
  useEffect(() => {
    setTitle('Distributor Assignment');
    setSubtitle('Map distributors to primary Field Sales Representatives');
  }, [setTitle, setSubtitle]);

  const PAGE_SIZE = 50;

  const fsrQuery = useQuery(GET_FSRS, {
    variables: { page: 1, limit: 200 },
    fetchPolicy: 'cache-and-network',
  });
  const distQuery = useQuery(GET_DISTRIBUTORS, {
    variables: { page: 1, limit: PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const [loadedPage, setLoadedPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  // When the underlying data is refetched (filters / refresh), reset pagination
  useEffect(() => {
    if (distQuery.data?.getDistributors?.page) {
      setLoadedPage(distQuery.data.getDistributors.page);
    }
  }, [distQuery.data?.getDistributors?.page]);

  const loadMoreDistributors = async () => {
    const total = distQuery.data?.getDistributors?.total ?? 0;
    if (loadingMore) return;
    if ((distQuery.data?.getDistributors?.customers?.length ?? 0) >= total) return;
    setLoadingMore(true);
    try {
      const next = loadedPage + 1;
      await distQuery.fetchMore({
        variables: { page: next, limit: PAGE_SIZE },
        updateQuery: (prev: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult?.getDistributors) return prev;
          const merged = [
            ...(prev?.getDistributors?.customers ?? []),
            ...fetchMoreResult.getDistributors.customers,
          ];
          // Dedupe by id (safety against overlapping pages)
          const seen = new Set<string>();
          const customers = merged.filter((c: any) => {
            if (seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
          });
          return {
            getDistributors: {
              ...fetchMoreResult.getDistributors,
              customers,
            },
          };
        },
      });
      setLoadedPage(next);
    } finally {
      setLoadingMore(false);
    }
  };

  const fsrs: LiveFsr[] = (fsrQuery.data?.getFsrs?.fsrs ?? []).map((f: any) => ({
    id: f.id,
    name: f.name,
    employeeId: f.employeeId,
    avatar: f.avatar,
    color: f.color,
    assignedDistributors: (f.assignedDistributors ?? []).map((a: any) => ({
      distributorId: a.distributorId,
      catalogAccess: a.catalogAccess === 'collection' ? 'collection' : 'full',
    })),
  }));

  const remoteDistributors: LiveDistributor[] = useMemo(
    () =>
      (distQuery.data?.getDistributors?.customers ?? []).map((c: any) => {
        const display =
          c.displayName ||
          c.companyName ||
          `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() ||
          c.email ||
          'Unnamed';
        return {
          id: c.id,
          name: display,
          city: c.city || '—',
          email: c.email,
          mobile: c.mobile,
          status: c.status,
          avatar: initialsOf(display),
          color: colorFor(c.id),
        } as LiveDistributor;
      }),
    [distQuery.data],
  );

  // distributorId → { fsrId, catalogAccess } reverse-index built from each FSR's assignedDistributors
  const assignmentIndex = useMemo(() => {
    const idx: Record<string, { fsrId: string; catalogAccess: 'full' | 'collection' }> = {};
    fsrs.forEach((f) => {
      (f.assignedDistributors || []).forEach((a) => {
        idx[a.distributorId] = { fsrId: f.id, catalogAccess: a.catalogAccess };
      });
    });
    return idx;
  }, [fsrs]);

  const distributors: LiveDistributor[] = remoteDistributors.map((d) => {
    const a = assignmentIndex[d.id];
    if (!a) return d;
    const officer = fsrs.find((f) => f.id === a.fsrId);
    return {
      ...d,
      officerId: officer?.id ?? null,
      officerName: officer?.name ?? null,
      catalogAccess: a.catalogAccess,
    };
  });

  const [setFsrDistributors, { loading: saving }] = useMutation(SET_FSR_DISTRIBUTORS);

  const [selectedFSRId, setSelectedFSRId] = useState<string | null>(null);
  const [fsrSearch, setFsrSearch] = useState('');
  const [showAssignPanel, setShowAssignPanel] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [tempDistIds, setTempDistIds] = useState<string[]>([]);
  const [tempCatalogMode, setTempCatalogMode] = useState<'full' | 'collection'>('full');

  const selectedFSR = fsrs.find((f) => f.id === selectedFSRId) ?? null;
  const currentAssignedDistributors = distributors.filter((d) => d.officerId === selectedFSRId);

  const handleSync = () => {
    showToast('Refreshing distributor list…', 'info');
    setLoadedPage(1);
    distQuery.refetch({ page: 1, limit: PAGE_SIZE });
    fsrQuery.refetch();
  };

  const openAssignPanel = () => {
    if (!selectedFSRId) return;
    const myCurrent = fsrs.find((f) => f.id === selectedFSRId)?.assignedDistributors ?? [];
    setTempDistIds(myCurrent.map((a) => a.distributorId));
    // Default the panel's catalog-mode toggle to whatever the majority of current assignments use
    const collectionCount = myCurrent.filter((a) => a.catalogAccess === 'collection').length;
    setTempCatalogMode(collectionCount > myCurrent.length / 2 ? 'collection' : 'full');
    setShowAssignPanel(true);
  };

  const toggleDistSelection = (id: string) =>
    setTempDistIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const saveAssignments = async () => {
    if (!selectedFSRId) return;
    const distributorsPayload = tempDistIds.map((id) => ({
      distributorId: id,
      catalogAccess: tempCatalogMode,
    }));
    try {
      const { data } = await setFsrDistributors({
        variables: { fsrId: selectedFSRId, distributors: distributorsPayload },
      });
      const out = data?.setFsrDistributors;
      if (out?.error) {
        showToast(out.message || 'Failed to save assignments', 'error');
        return;
      }
      // Refetch FSRs so every other FSR's count + this FSR's list refresh
      await fsrQuery.refetch();
      setShowAssignPanel(false);
      showToast(`Updated assignments for ${selectedFSR?.name}`, 'success');
    } catch (err: any) {
      showToast(err?.message || 'Failed to save assignments', 'error');
    }
  };

  const removeDistributor = async (distId: string) => {
    if (!selectedFSRId) return;
    const myCurrent = fsrs.find((f) => f.id === selectedFSRId)?.assignedDistributors ?? [];
    const remaining = myCurrent
      .filter((a) => a.distributorId !== distId)
      .map((a) => ({ distributorId: a.distributorId, catalogAccess: a.catalogAccess }));
    try {
      const { data } = await setFsrDistributors({
        variables: { fsrId: selectedFSRId, distributors: remaining },
      });
      if (data?.setFsrDistributors?.error) {
        showToast(data.setFsrDistributors.message || 'Failed to remove', 'error');
        return;
      }
      await fsrQuery.refetch();
    } catch (err: any) {
      showToast(err?.message || 'Failed to remove', 'error');
    }
  };

  const loading = distQuery.loading && remoteDistributors.length === 0;
  const apiError = distQuery.data?.getDistributors?.error
    ? distQuery.data.getDistributors.message
    : distQuery.error?.message || null;

  return (
    <div className="space-y-6 page-fade-in pb-10">
      {/* SUMMARY */}
      <div className="grid grid-cols-4 gap-4">
        {[
          {
            label: 'Synced Partners',
            value: remoteDistributors.length,
            sub: 'B2B Node Connected',
            icon: Network,
            color: 'from-indigo-600 to-indigo-700',
          },
          {
            label: 'Assigned Nodes',
            value: distributors.filter((d) => d.officerId).length,
            sub: 'Mapping Complete',
            icon: CheckCircle2,
            color: 'from-emerald-600 to-emerald-700',
          },
          {
            label: 'Active FSRs',
            value: fsrs.length,
            sub: 'Field Officers',
            icon: Users,
            color: 'from-amber-600 to-amber-700',
          },
          {
            label: 'Unassigned',
            value: remoteDistributors.length - distributors.filter((d) => d.officerId).length,
            sub: 'Pending Assignment',
            icon: AlertCircle,
            color: 'from-rose-600 to-rose-700',
          },
        ].map((stat, i) => (
          <div
            key={i}
            className="bg-white p-5 rounded-3xl border border-white shadow-sm flex items-center gap-4 transition-transform hover:scale-[1.02] cursor-default"
          >
            <div
              className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-white shadow-lg`}
            >
              <stat.icon size={22} strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
                {stat.label}
              </p>
              <div className="text-2xl font-black text-slate-900 tracking-tight leading-none">{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      {apiError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>Couldn't load distributors from B2B: {apiError}</span>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6 h-[700px]">
        {/* FSR LIST */}
        <div className="col-span-4 bg-white border border-white rounded-[32px] shadow-xl flex flex-col overflow-hidden">
          <div className="p-6 border-b border-slate-50">
            <h3 className="text-lg font-black text-slate-900 tracking-tight mb-4 flex items-center gap-2">
              <Users size={20} className="text-indigo-600" /> Field Assist Officers
            </h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search officers..."
                className="w-full h-11 pl-10 pr-4 bg-slate-50 border-2 border-transparent rounded-2xl outline-none focus:border-indigo-500/30 text-[13px] font-bold transition-all shadow-inner"
                value={fsrSearch}
                onChange={(e) => setFsrSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2.5">
            {fsrQuery.loading && fsrs.length === 0 ? (
              <div className="text-center text-slate-400 text-sm py-12">Loading FSRs…</div>
            ) : fsrs.length === 0 ? (
              <div className="text-center text-slate-400 text-sm py-12">
                No field users yet. Add one in Field Users first.
              </div>
            ) : (
              fsrs
                .filter((f) => f.name.toLowerCase().includes(fsrSearch.toLowerCase()))
                .map((fsr) => {
                  const isSelected = selectedFSRId === fsr.id;
                  const assignedCount = distributors.filter((d) => d.officerId === fsr.id).length;
                  return (
                    <button
                      key={fsr.id}
                      onClick={() => setSelectedFSRId(fsr.id)}
                      className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all group
                        ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl shadow-indigo-200' : 'bg-white border-transparent hover:bg-slate-50 text-slate-900'}`}
                    >
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-sm shadow-lg"
                        style={{ backgroundColor: fsr.color || '#4F46E5' }}
                      >
                        {fsr.avatar || initialsOf(fsr.name)}
                      </div>
                      <div className="flex-1 text-left">
                        <div
                          className={`text-[15px] font-black tracking-tight leading-none mb-1 ${
                            isSelected ? 'text-white' : 'text-slate-900'
                          }`}
                        >
                          {fsr.name}
                        </div>
                        <div className={`text-[11px] font-bold ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                          {assignedCount} {assignedCount === 1 ? 'Node' : 'Nodes'} Assigned · {fsr.employeeId}
                        </div>
                      </div>
                      <ChevronRight size={18} className={isSelected ? 'text-white/50' : 'text-slate-300'} />
                    </button>
                  );
                })
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-span-8 bg-white border border-white rounded-[32px] shadow-xl flex flex-col overflow-hidden relative">
          <AnimatePresence mode="wait">
            {selectedFSR ? (
              <motion.div
                key={`fsr-${selectedFSR.id}`}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex flex-col h-full"
              >
                <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/30">
                  <div className="flex items-center gap-5">
                    <div
                      className="w-16 h-16 rounded-3xl flex items-center justify-center text-white font-black text-2xl shadow-xl"
                      style={{ backgroundColor: selectedFSR.color || '#4F46E5' }}
                    >
                      {selectedFSR.avatar || initialsOf(selectedFSR.name)}
                    </div>
                    <div>
                      <div className="text-[11px] font-black text-indigo-600 uppercase tracking-[0.25em] mb-1">
                        Mapping for
                      </div>
                      <h2 className="text-3xl font-black text-slate-900 tracking-tighter italic leading-none">
                        {selectedFSR.name}
                      </h2>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSync}
                      className="h-12 px-5 bg-white border-2 border-slate-100 rounded-2xl flex items-center gap-2 text-[13px] font-black text-slate-600 hover:border-indigo-100 uppercase tracking-widest transition-all"
                    >
                      <RefreshCw size={18} className={distQuery.loading || fsrQuery.loading ? 'animate-spin' : ''} /> Sync
                    </button>
                    <button
                      onClick={openAssignPanel}
                      className="h-12 px-6 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-2xl flex items-center gap-2 text-[13px] font-black shadow-lg hover:shadow-indigo-200 uppercase tracking-widest active:scale-95 transition-all"
                    >
                      <Plus size={18} strokeWidth={3} /> Assign Nodes
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                  {currentAssignedDistributors.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
                      <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mb-6 border-4 border-dashed border-slate-200">
                        <Network size={48} />
                      </div>
                      <h4 className="text-xl font-black text-slate-900 tracking-tight italic">No Partnerships Assigned</h4>
                      <p className="text-[14px] font-medium text-slate-500 max-w-xs mt-2 italic leading-relaxed">
                        This officer doesn't have any distributors assigned for field visits yet.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {currentAssignedDistributors.map((dist) => (
                        <div
                          key={dist.id}
                          className="bg-white border-2 border-slate-50 rounded-[32px] p-6 shadow-sm hover:shadow-xl hover:border-indigo-50 transition-all group overflow-hidden relative"
                        >
                          <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50/30 rounded-full -mr-8 -mt-8" />
                          <div className="flex items-center gap-4 mb-6">
                            <div
                              className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-lg shadow-lg group-hover:rotate-6 transition-transform"
                              style={{ backgroundColor: dist.color }}
                            >
                              {dist.avatar}
                            </div>
                            <div className="min-w-0">
                              <div className="text-[11px] font-black text-indigo-600 uppercase tracking-wider mb-1 opacity-70 italic">
                                {dist.id.slice(-6).toUpperCase()}
                              </div>
                              <h4 className="text-lg font-black text-slate-900 truncate leading-none italic">
                                {dist.name}
                              </h4>
                            </div>
                          </div>

                          <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 text-[12px] text-slate-600">
                              <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500 shrink-0">
                                <Mail size={13} />
                              </div>
                              <span className="font-semibold truncate">{dist.email || <span className="text-slate-300 italic">No email</span>}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[12px] text-slate-600">
                              <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500 shrink-0">
                                <Phone size={13} />
                              </div>
                              <span className="font-semibold">{dist.mobile || <span className="text-slate-300 italic">No mobile</span>}</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                            <span
                              className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border
                                ${dist.catalogAccess === 'full' ? 'bg-indigo-50 border-indigo-100 text-indigo-600' : 'bg-amber-50 border-amber-100 text-amber-600'}`}
                            >
                              {dist.catalogAccess === 'full' ? 'Full B2B' : 'Restricted'}
                            </span>
                            <button
                              onClick={() => removeDistributor(dist.id)}
                              className="w-9 h-9 rounded-xl border border-slate-100 flex items-center justify-center text-slate-300 hover:text-rose-500 hover:bg-rose-50 hover:border-rose-100 transition-all"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-20 opacity-40">
                <div className="w-32 h-32 bg-slate-50 rounded-[40px] flex items-center justify-center text-slate-300 mb-8 border-4 border-dashed border-slate-100 rotate-12">
                  <UserCheck size={64} />
                </div>
                <h3 className="text-[28px] font-black text-slate-900 tracking-tighter italic">Select an Officer</h3>
                <p className="text-[16px] font-medium text-slate-500 max-w-sm mt-3 italic leading-relaxed">
                  Choose a Field Assist Representative from the list on the left to manage their distributor network and
                  catalog permissions.
                </p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ASSIGN SLIDE PANEL */}
      <SlidePanel
        isOpen={showAssignPanel}
        onClose={() => setShowAssignPanel(false)}
        width={580}
        title="Distributor Node Selection"
        subtitle={`Select nodes to assign to ${selectedFSR?.name ?? ''}`}
      >
        <div className="space-y-8 pb-10">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder={`Search across ${remoteDistributors.length} synced partners...`}
              className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-transparent rounded-[24px] outline-none focus:border-indigo-500/30 text-[15px] font-bold transition-all shadow-inner italic"
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
            />
          </div>

          <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-2 pt-2">
            <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.25em] mb-4 pl-1">
              AVAILABLE B2B NODES
            </h4>
            {loading ? (
              <div className="text-center text-slate-400 text-sm py-12">Loading distributors…</div>
            ) : (
              distributors
                .filter((d) => d.name.toLowerCase().includes(assignSearch.toLowerCase()))
                .map((dist) => {
                  const isAssignedToOther = dist.officerId && dist.officerId !== selectedFSRId;
                  const isSelected = tempDistIds.includes(dist.id);
                  return (
                    <button
                      key={dist.id}
                      disabled={!!isAssignedToOther}
                      onClick={() => toggleDistSelection(dist.id)}
                      className={`w-full flex items-center gap-4 p-5 rounded-[32px] border-2 transition-all relative overflow-hidden group
                        ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl' : 'bg-white border-slate-50 hover:border-indigo-100 hover:bg-slate-50'}
                        ${isAssignedToOther ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                    >
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-lg"
                        style={{ backgroundColor: isSelected ? '#ffffff20' : dist.color }}
                      >
                        {dist.avatar}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[15px] font-black tracking-tight italic ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                            {dist.name}
                          </span>
                          {isAssignedToOther && (
                            <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-[8px] font-black rounded uppercase">
                              {dist.officerName}
                            </span>
                          )}
                        </div>
                        <div className={`text-[11px] font-bold flex flex-col gap-1 ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                          {dist.mobile && (
                            <span className="flex items-center gap-1">
                              <Phone size={10} /> {dist.mobile}
                            </span>
                          )}
                          {dist.email && (
                            <span className="flex items-center gap-1 truncate">
                              <Mail size={10} /> <span className="truncate">{dist.email}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        className={`w-7 h-7 rounded-full border-3 flex items-center justify-center transition-all
                          ${isSelected ? 'bg-white border-white' : 'bg-white border-slate-100 shadow-inner'}`}
                      >
                        {isSelected && <Check size={16} className="text-indigo-600" strokeWidth={4} />}
                      </div>
                    </button>
                  );
                })
            )}

            {!loading && (() => {
              const total = distQuery.data?.getDistributors?.total ?? 0;
              const loaded = remoteDistributors.length;
              if (loaded >= total) return null;
              return (
                <div className="pt-4 flex flex-col items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-400">
                    Showing {loaded} of {total}
                  </span>
                  <button
                    onClick={loadMoreDistributors}
                    disabled={loadingMore}
                    className="h-11 px-6 rounded-full border-2 border-indigo-100 bg-white text-indigo-600 font-black text-[12px] uppercase tracking-widest hover:bg-indigo-50 transition disabled:opacity-60 flex items-center gap-2"
                  >
                    {loadingMore ? (
                      <>
                        <RefreshCw size={14} className="animate-spin" /> Loading…
                      </>
                    ) : (
                      <>Show more ({Math.min(PAGE_SIZE, total - loaded)})</>
                    )}
                  </button>
                </div>
              );
            })()}
          </div>

          <div className="bg-slate-50/50 p-6 rounded-[32px] border-2 border-slate-50">
            <h4 className="text-[12px] font-black text-slate-900 uppercase tracking-[0.25em] mb-4 italic">Catalog Access</h4>
            <div className="grid grid-cols-2 gap-3">
              {(
                [
                  { id: 'full', label: 'Full B2B Sync', desc: 'Sync entire library' },
                  { id: 'collection', label: 'Bundle Access', desc: 'Specific collections' },
                ] as const
              ).map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => setTempCatalogMode(mode.id)}
                  className={`p-5 rounded-2xl border-2 text-left transition-all
                    ${tempCatalogMode === mode.id ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg' : 'bg-white border-white hover:border-indigo-100'}`}
                >
                  <div className="text-[15px] font-black mb-1">{mode.label}</div>
                  <div className={`text-[11px] ${tempCatalogMode === mode.id ? 'text-white/70' : 'text-slate-400'}`}>{mode.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-4 pt-4 sticky bottom-0 bg-white pb-10">
            <button
              onClick={() => setShowAssignPanel(false)}
              className="flex-1 h-14 bg-slate-100 text-slate-500 font-black text-[15px] rounded-2xl uppercase tracking-widest hover:bg-slate-200 transition-all italic"
            >
              Cancel
            </button>
            <button
              onClick={saveAssignments}
              disabled={saving}
              className="flex-[2] h-14 rounded-2xl font-black text-[15px] uppercase tracking-widest shadow-2xl active:scale-95 transition-all flex items-center justify-center gap-3 italic bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-indigo-200 disabled:opacity-60"
            >
              {saving ? 'Saving…' : (<>Commit Assignments <Check size={20} strokeWidth={3} /></>)}
            </button>
          </div>
        </div>
      </SlidePanel>
    </div>
  );
};
